/** Retiradas: cola `requested` → aprobar (Transfer + Payout) o rechazar (vuelve al wallet). */
import { useEffect, useState, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, limit, orderBy, query, where } from 'firebase/firestore';
import { api } from '@nuvvu/shared/api';
import { call, errorText } from '../api.js';
import { db } from '../firebase.js';
import { btn, field, muted, Notice, Table, td, when, money } from '../lib/ui.js';

type Doc = Record<string, unknown>;

export function WithdrawalsPage(): ReactElement {
  const [status, setStatus] = useState('requested');
  const [rows, setRows] = useState<Doc[] | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [version, setVersion] = useState(0);
  useEffect(() => {
    setRows(null);
    getDocs(
      query(
        collection(db, 'withdrawals'),
        where('status', '==', status),
        orderBy('created_at', status === 'requested' ? 'asc' : 'desc'),
        limit(100),
      ),
    )
      .then((s) => {
        setRows(s.docs.map((d) => ({ id: d.id, ...d.data() })));
      })
      .catch((e: unknown) => {
        setError(errorText(e));
        setRows([]);
      });
  }, [status, version]);
  const review = (id: string, action: 'approve' | 'reject'): void => {
    const reason =
      action === 'reject' ? (window.prompt('Motivo del rechazo (lo ve el vendedor):') ?? '') : '';
    if (action === 'reject' && !reason) return;
    if (action === 'approve' && !window.confirm('¿Aprobar y pagar esta retirada?')) return;
    setError(null);
    call(api.admin.reviewWithdrawal, { withdrawal_id: id, action, ...(reason ? { reason } : {}) })
      .then((r) => {
        setNotice(`Retirada ${r.status}.`);
        setVersion((v) => v + 1);
      })
      .catch((e: unknown) => {
        setError(errorText(e));
      });
  };
  return (
    <>
      <h1 style={{ margin: 0 }}>Retiradas</h1>
      <select
        style={{ ...field, width: 'auto' }}
        value={status}
        onChange={(e) => {
          setStatus(e.target.value);
        }}
      >
        {['requested', 'approved', 'paid', 'rejected', 'failed'].map((s) => (
          <option key={s} value={s}>
            {s}
          </option>
        ))}
      </select>
      <Notice text={notice} />
      <Notice text={error} error />
      {!rows && <p style={muted}>Cargando…</p>}
      {rows && (
        <Table head={['Fecha', 'Usuario', 'Importe', 'Recibe', 'FX', 'Método', 'Estado', '']}>
          {rows.length === 0 && (
            <tr>
              <td style={td} colSpan={8}>
                Nada en esta cola.
              </td>
            </tr>
          )}
          {rows.map((w) => (
            <tr key={String(w.id)}>
              <td style={td}>{when(w.created_at)}</td>
              <td style={td}>
                <Link to={`/usuarios/${String(w.uid)}`}>{String(w.uid).slice(-8)}</Link>
              </td>
              <td style={td}>{money(w.amount)}</td>
              <td style={td}>{money(w.payout)}</td>
              <td style={td}>
                {(w.fx_surcharge as Doc | undefined)?.amount
                  ? `${money(w.fx_surcharge)} (${String(w.fx_rate)})`
                  : '—'}
              </td>
              <td style={td}>{String(w.method)}</td>
              <td style={td}>
                {String(w.status)}
                {typeof w.last_error === 'string' ? ` · ${w.last_error}` : ''}
              </td>
              <td style={td}>
                {w.status === 'requested' && (
                  <span style={{ display: 'flex', gap: 6 }}>
                    <button
                      type="button"
                      style={btn(true)}
                      onClick={() => {
                        review(String(w.id), 'approve');
                      }}
                    >
                      Aprobar
                    </button>
                    <button
                      type="button"
                      style={btn(false, true)}
                      onClick={() => {
                        review(String(w.id), 'reject');
                      }}
                    >
                      Rechazar
                    </button>
                  </span>
                )}
              </td>
            </tr>
          ))}
        </Table>
      )}
    </>
  );
}
